import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import redirectLinks from 'constants/redirectLinks';
import ReturnButton from 'components/system/atoms/buttons/ReturnButton';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import MsgHelper from 'utils/message.helper';
import MSG from 'constants/messages';
import DecisionButton from 'components/machineConf/atoms/DecisionButton';
import OutlineTextBox from 'components/system/atoms/textBoxs/OutlineTextBox';
import Combobox, { ListBoxItemType } from 'components/system/atoms/selectBoxs/Combobox';
import ConfirmDialog from 'components/system/organisms/dialogs/ConfirmDialog';
import InfoDialog from 'components/system/organisms/dialogs/InfoDialog';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';
import { EquipmentSelectDataTableRow } from 'types/group/EquipmentSelectType';
import { MachineListApiResponseType } from 'types/group/GroupDataStateType';
import { MachineGiftNameResponseType } from 'types/maintenance/MachineType';
import { themeColor } from 'constants/theme';
import COMMON from 'constants/common';
import { renderMachineType } from 'utils/common.helper';
import { setGiftNameListDataSource } from '../../../redux/slices/maintenanceSlice';

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  content: {
    height: '598px',
    width: '1140px',
    padding: '0 16px',
  },
  topArea: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& .returnButton': {
      flex: 1,
      padding: '10px 0px 0px 0px',
      width: '224px',
    },
    '& .addNewButton': {
      height: '64px',
      marginLeft: '28px',
    },
  },
  mainArea: {
    height: '430px',
    flexDirection: 'row',
    marginTop: 24,
  },
  decision: {
    textAlign: 'right',
  },
  addNewButton: {
    height: '64px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 24,
  },
  multiInputRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 4,
  },
  multiInputRow2: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 4,
  },
  labelStyle: {
    fontSize: 28,
    color: '#000000',
    marginRight: 24,
    width: 217,
  },
  multiLabelStyle: {
    fontSize: 28,
    color: '#000000',
    marginRight: 8,
    width: 250,
  },
  duoLabelStyle: {
    fontSize: 28,
    color: '#000000',
    marginRight: 8,
    width: 260,
  },
  labelContent: {
    textAlign: 'right',
  },
  multiLabelContent: {
    textAlign: 'right',
    width: 250,
  },
  modelNameLabelStyle: {
    fontSize: 28,
    color: '#000000',
    marginRight: 8,
  },
  modelName: {
    width: 'auto',
    marginRight: 24,
  },
  textInputLeftStationName: {
    '& .outlinedInput': {
      border: '2px solid',
      borderColor: themeColor.leftDarkColor,
      borderRadius: '4px',
      height: '48px',
      width: 645,
      '& input': {
        fontSize: 28,
      },
    },
  },
  textInputRightStationName: {
    '& .outlinedInput': {
      border: '2px solid',
      borderColor: themeColor.rightDarkColor,
      borderRadius: '4px',
      height: '48px',
      width: 645,
      '& input': {
        fontSize: 28,
      },
    },
  },
  textInputRightGrandStationName: {
    '& .outlinedInput': {
      border: '2px solid',
      borderColor: themeColor.grandDarkColor,
      borderRadius: '4px',
      height: '48px',
      width: 645,
      '& input': {
        fontSize: 28,
      },
    },
  },
  leftGiftComboboxOutline: {
    border: '2px solid',
    borderColor: themeColor.leftDarkColor,
    borderRadius: '4px',
    margin: '4px 0 0 0',
    height: 48,
    width: 645,
  },
  rightGiftComboboxOutline: {
    border: '2px solid',
    borderColor: themeColor.rightDarkColor,
    borderRadius: '4px',
    margin: '4px 0 0 0',
    height: 48,
    width: 645,
  },
  rightGiftGrandComboboxOutline: {
    border: '2px solid',
    borderColor: themeColor.grandDarkColor,
    borderRadius: '4px',
    margin: '4px 0 0 0',
    height: 48,
    width: 645,
  },
  comboboxTextInput: {
    '& #leftGiftName': {
      fontSize: 28,
      padding: '6px 4px 6px 8px',
    },
    '& #rightGiftName': {
      fontSize: 28,
      padding: '6px 4px 6px 8px',
    },
  },
  contentText: { fontSize: '24px' },
}));

type UpdateMachineValueType = {
  machineNo: string;
  machineType: string | null;
  leftStationName: string;
  leftGiftId?: number | string | null;
  rightStationName: string;
  rightGiftId?: number | string | null;
  leftGiftDefault?: ListBoxItemType | null;
  rightGiftDefault?: ListBoxItemType | null;
  st1StationName: string;
  st1GiftId?: number | string | null;
  st1GiftDefault?: ListBoxItemType | null;
  st2StationName: string;
  st2GiftId?: number | string | null;
  st2GiftDefault?: ListBoxItemType | null;
  st3StationName: string;
  st3GiftId?: number | string | null;
  st3GiftDefault?: ListBoxItemType | null;
  st4StationName: string;
  st4GiftId?: number | string | null;
  st4GiftDefault?: ListBoxItemType | null;
};

type SubmitResponseType = {
  result: boolean;
};

type ParametersType = {
  boardSerial: string;
};

// messages
const confirmMsgSave: string = MsgHelper.messageFormat(MSG.INF230);
const messageSaveSuccess: string = MsgHelper.messageFormat(MSG.INF309, '機器', '機器一覧');

const UpdateMachine: React.FC<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const loginUserInfo = useAppSelector((state) => state.common.userInfo);
  const boardSerialParameter =
    location.state !== undefined && location.state !== null ? (location.state as ParametersType).boardSerial : '';
  const [machineGiftNameDatasource, setMachineGiftNameDatasource] = React.useState<Array<ListBoxItemType>>([]);
  const [openSaveConfirmDlg, setOpenSaveConfirmDlg] = React.useState(false);
  const [openInfoDialog, setOpenInfoDialog] = React.useState(false);
  const [apiCompleted, setApiCompleted] = React.useState(false);
  const [inputValue, setInputValue] = useState<UpdateMachineValueType>({
    machineNo: '',
    machineType: null,
    leftStationName: '',
    leftGiftId: undefined,
    rightStationName: '',
    rightGiftId: undefined,
    leftGiftDefault: undefined,
    rightGiftDefault: undefined,
    st1StationName: '',
    st1GiftId: undefined,
    st1GiftDefault: undefined,
    st2StationName: '',
    st2GiftId: undefined,
    st2GiftDefault: undefined,
    st3StationName: '',
    st3GiftId: undefined,
    st3GiftDefault: undefined,
    st4StationName: '',
    st4GiftId: undefined,
    st4GiftDefault: undefined,
  });

  const getGiftNameList = async (): Promise<Array<ListBoxItemType>> => {
    const http = new HttpConnection({ dispatch }, false);
    const data = await http.get<Array<MachineGiftNameResponseType>>(REST_API.MAINTENANCE.GET_MACHINE_GIFT_NAME_LIST);
    let machineGiftNameListFormat: Array<ListBoxItemType> = [];
    if (data.length > 0) {
      machineGiftNameListFormat = data.map((giftName) => ({
        id: giftName.giftId,
        title: giftName.giftName,
      }));
    }
    dispatch(setGiftNameListDataSource(data));
    return Promise.resolve<Array<ListBoxItemType>>(machineGiftNameListFormat);
  };

  const getMachineDetail = async (boardSerial: string): Promise<EquipmentSelectDataTableRow | null> => {
    const http = new HttpConnection({ dispatch });
    const data = await http.get<MachineListApiResponseType>(REST_API.MAINTENANCE.GET_MACHINE_DETAIL_BY_BOARD_SERIAL, {
      boardSerial,
    });
    let machineDetail: EquipmentSelectDataTableRow | null = null;
    if (data) {
      machineDetail = {
        key: data.boardSerial,
        checked: false,
        machineNo: data.boardSerial,
        machineType: data.machineType,
        machineName: data.machineName,
        leftStationKey: data.leftStationId,
        leftStationName: data.leftStationName,
        leftStationGiftName: data.leftGiftName,
        leftStationGiftKey: data.leftGiftId,
        rightStationKey: data.rightStationId,
        rightStationName: data.rightStationName,
        rightStationGiftName: data.rightGiftName,
        rightStationGiftKey: data.rightGiftId,
        st1StationKey: data.st1StationId,
        st1StationName: data.st1StationName,
        st1StationGiftName: data.st1GiftName,
        st1StationGiftKey: data.st1GiftId,
        st2StationKey: data.st2StationId,
        st2StationName: data.st2StationName,
        st2StationGiftName: data.st2GiftName,
        st2StationGiftKey: data.st2GiftId,
        st3StationKey: data.st3StationId,
        st3StationName: data.st3StationName,
        st3StationGiftName: data.st3GiftName,
        st3StationGiftKey: data.st3GiftId,
        st4StationKey: data.st4StationId,
        st4StationName: data.st4StationName,
        st4StationGiftName: data.st4GiftName,
        st4StationGiftKey: data.st4GiftId,
      };
    }
    return Promise.resolve<EquipmentSelectDataTableRow | null>(machineDetail);
  };

  const handleGetMachineDetail = (boardSerial: string) => {
    getMachineDetail(boardSerial).then((machineDetail: EquipmentSelectDataTableRow | null) => {
      if (machineDetail !== null) {
        getGiftNameList().then((giftNameList: Array<ListBoxItemType>) => {
          setMachineGiftNameDatasource(giftNameList);
          const leftGiftNameItem: ListBoxItemType =
            giftNameList.filter((machineGift) => machineGift.id === machineDetail.leftStationGiftKey)[0] ?? undefined;
          const rightGiftNameItem: ListBoxItemType =
            giftNameList.filter((machineGift) => machineGift.id === machineDetail.rightStationGiftKey)[0] ?? undefined;
          const st1GiftNameItem: ListBoxItemType =
            giftNameList.filter((machineGift) => machineGift.id === machineDetail.st1StationGiftKey)[0] ?? undefined;
          const st2GiftNameItem: ListBoxItemType =
            giftNameList.filter((machineGift) => machineGift.id === machineDetail.st2StationGiftKey)[0] ?? undefined;
          const st3GiftNameItem: ListBoxItemType =
            giftNameList.filter((machineGift) => machineGift.id === machineDetail.st3StationGiftKey)[0] ?? undefined;
          const st4GiftNameItem: ListBoxItemType =
            giftNameList.filter((machineGift) => machineGift.id === machineDetail.st4StationGiftKey)[0] ?? undefined;
          setInputValue({
            ...inputValue,
            machineNo: machineDetail.machineNo,
            machineType: machineDetail.machineType,
            leftStationName: machineDetail.leftStationName,
            leftGiftId: machineDetail.leftStationGiftKey,
            rightStationName: machineDetail.rightStationName,
            rightGiftId: machineDetail.rightStationGiftKey,
            leftGiftDefault: leftGiftNameItem,
            rightGiftDefault: rightGiftNameItem,
            st1StationName: machineDetail.st1StationName,
            st1GiftId: machineDetail.st1StationGiftKey,
            st1GiftDefault: st1GiftNameItem,
            st2StationName: machineDetail.st2StationName,
            st2GiftId: machineDetail.st2StationGiftKey,
            st2GiftDefault: st2GiftNameItem,
            st3StationName: machineDetail.st3StationName,
            st3GiftId: machineDetail.st3StationGiftKey,
            st3GiftDefault: st3GiftNameItem,
            st4StationName: machineDetail.st4StationName,
            st4GiftId: machineDetail.st4StationGiftKey,
            st4GiftDefault: st4GiftNameItem,
          });
          setApiCompleted(true);
        });
      }
    });
  };

  useEffect(() => {
    handleGetMachineDetail(boardSerialParameter);
  }, [boardSerialParameter]);

  const handleClickGroupConfTop = () => {
    navigate(redirectLinks.MAINT_MACHINE_LIST);
  };

  const handleSaveButton = () => {
    setOpenSaveConfirmDlg(true);
  };
  const handleChangeInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue({
      ...inputValue,
      [ev.target.name]: ev.target.value,
    });
  };

  const handleGiftNameChange = (value: ListBoxItemType | null, id: string) => {
    let newValue = null;
    if (value !== null && value !== undefined) {
      newValue = value.id;
    }
    if (id === 'leftGiftId') {
      setInputValue({ ...inputValue, [id]: newValue, leftGiftDefault: value });
    } else if (id === 'st1GiftId') {
      setInputValue({ ...inputValue, [id]: newValue, st1GiftDefault: value });
    } else if (id === 'st2GiftId') {
      setInputValue({ ...inputValue, [id]: newValue, st2GiftDefault: value });
    } else if (id === 'st3GiftId') {
      setInputValue({ ...inputValue, [id]: newValue, st3GiftDefault: value });
    } else if (id === 'st4GiftId') {
      setInputValue({ ...inputValue, [id]: newValue, st4GiftDefault: value });
    } else {
      setInputValue({ ...inputValue, [id]: newValue, rightGiftDefault: value });
    }
  };

  const apiSubmitUpdateMachine = async () => {
    if (!(loginUserInfo?.currentHeadQId && loginUserInfo?.currentStoreId)) return;
    const machineUpdateBody = {
      boardSerial: inputValue.machineNo,
      machineType: inputValue.machineType,
      leftStationName: inputValue.leftStationName,
      leftGiftId: inputValue.leftGiftId,
      rightStationName: inputValue.rightStationName,
      rightGiftId: inputValue.rightGiftId,
      st1StationName: inputValue.st1StationName,
      st1GiftId: inputValue.st1GiftId,
      st2StationName: inputValue.st2StationName,
      st2GiftId: inputValue.st2GiftId,
      st3StationName: inputValue.st3StationName,
      st3GiftId: inputValue.st3GiftId,
      st4StationName: inputValue.st4StationName,
      st4GiftId: inputValue.st4GiftId,
    };
    const http = new HttpConnection({ dispatch });
    const data = await http.post<SubmitResponseType>(REST_API.MAINTENANCE.GET_MACHINE_UPDATE_BY_BOARD_SERIAL, {
      ...machineUpdateBody,
    });
    if (data.result) {
      setOpenInfoDialog(true);
    }
  };

  const handleSubmitData = useCallback(() => {
    setOpenSaveConfirmDlg(false);
    apiSubmitUpdateMachine();
  }, [inputValue]);

  const handleCloseInfoDialog = useCallback(() => {
    setOpenInfoDialog(false);
    handleClickGroupConfTop();
  }, []);

  return (
    <>
      <div className={classes.content}>
        <div className={classes.topArea}>
          <div className="returnButton">
            <ReturnButton onClick={handleClickGroupConfTop}>機器一覧</ReturnButton>
          </div>
        </div>
        <div className={classes.mainArea} style={{ visibility: apiCompleted ? 'visible' : 'hidden' }}>
          <div className={classes.inputRow}>
            <div
              className={
                inputValue.machineType === COMMON.MACHINE_TYPE.MULTI ? classes.multiLabelStyle : classes.labelStyle
              }>
              <div className={classes.labelContent}>登録番号</div>
            </div>
            <div className={classes.labelStyle}>
              <div>{inputValue.machineNo}</div>
            </div>
            {inputValue.machineType === COMMON.MACHINE_TYPE.MULTI && (
              <div className={classes.multiInputRow}>
                <div className={classes.modelNameLabelStyle}>
                  <div className={classes.modelName}>機種名</div>
                </div>
                <div className={classes.multiLabelStyle}>
                  <div>{renderMachineType(inputValue.machineType)}</div>
                </div>
              </div>
            )}
          </div>
          {inputValue.machineType === COMMON.MACHINE_TYPE.CLENA3 && (
            <div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>ステーション名L</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="leftStationName"
                    name="leftStationName"
                    value={inputValue.leftStationName}
                    onChange={handleChangeInput}
                    className={classes.textInputLeftStationName}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="leftGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.leftGiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'leftGiftId')}
                    classOutline={classes.leftGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>ステーション名R</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="rightStationName"
                    name="rightStationName"
                    value={inputValue.rightStationName}
                    onChange={handleChangeInput}
                    className={classes.textInputRightStationName}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="rightGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.rightGiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'rightGiftId')}
                    classOutline={classes.rightGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
            </div>
          )}

          {inputValue.machineType === COMMON.MACHINE_TYPE.JACK && (
            <div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>ステーション名</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="leftStationName"
                    name="leftStationName"
                    value={inputValue.leftStationName}
                    onChange={handleChangeInput}
                    className={classes.textInputRightStationName}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="leftGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.leftGiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'leftGiftId')}
                    classOutline={classes.rightGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
            </div>
          )}

          {inputValue.machineType === COMMON.MACHINE_TYPE.GRAND && (
            <div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>機種名</div>
                </div>
                <div className={classes.labelStyle}>
                  <div>{renderMachineType(inputValue.machineType)}</div>
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>ステーション名</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="leftStationName"
                    name="leftStationName"
                    value={inputValue.leftStationName}
                    onChange={handleChangeInput}
                    className={classes.textInputRightGrandStationName}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="leftGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.leftGiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'leftGiftId')}
                    classOutline={classes.rightGiftGrandComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
            </div>
          )}

          {inputValue.machineType === COMMON.MACHINE_TYPE.MULTI && (
            <div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>ステーション名ST1</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="st1StationName"
                    name="st1StationName"
                    value={inputValue.st1StationName}
                    onChange={handleChangeInput}
                    className={classes.textInputLeftStationName}
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="leftGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.st1GiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'st1GiftId')}
                    classOutline={classes.leftGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>ステーション名ST2</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="st2StationName"
                    name="st2StationName"
                    value={inputValue.st2StationName}
                    onChange={handleChangeInput}
                    className={classes.textInputRightStationName}
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="rightGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.st2GiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'st2GiftId')}
                    classOutline={classes.rightGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>ステーション名ST3</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="st3StationName"
                    name="st3StationName"
                    value={inputValue.st3StationName}
                    onChange={handleChangeInput}
                    className={classes.textInputLeftStationName}
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="leftGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.st3GiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'st3GiftId')}
                    classOutline={classes.leftGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                    placement="top-start"
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>ステーション名ST4</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="st4StationName"
                    name="st4StationName"
                    value={inputValue.st4StationName}
                    onChange={handleChangeInput}
                    className={classes.textInputRightStationName}
                  />
                </div>
              </div>
              <div className={classes.multiInputRow}>
                <div className={classes.multiLabelStyle}>
                  <div className={classes.multiLabelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="rightGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.st4GiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'st4GiftId')}
                    classOutline={classes.rightGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                    placement="top-start"
                  />
                </div>
              </div>
            </div>
          )}
          {inputValue.machineType === COMMON.MACHINE_TYPE.DUO && (
            <div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>機種名</div>
                </div>
                <div className={classes.duoLabelStyle}>
                  <div>{renderMachineType(inputValue.machineType)}</div>
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>ステーション名L</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="leftStationName"
                    name="leftStationName"
                    value={inputValue.leftStationName}
                    onChange={handleChangeInput}
                    className={classes.textInputLeftStationName}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="leftGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.leftGiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'leftGiftId')}
                    classOutline={classes.leftGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>ステーション名R</div>
                </div>
                <div>
                  <OutlineTextBox
                    id="rightStationName"
                    name="rightStationName"
                    value={inputValue.rightStationName}
                    onChange={handleChangeInput}
                    className={classes.textInputRightStationName}
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.labelStyle}>
                  <div className={classes.labelContent}>景品名称</div>
                </div>
                <div>
                  <Combobox
                    id="rightGiftName"
                    dataSource={machineGiftNameDatasource}
                    value={inputValue.rightGiftDefault}
                    onChange={(value) => handleGiftNameChange(value, 'rightGiftId')}
                    classOutline={classes.rightGiftComboboxOutline}
                    classInput={classes.comboboxTextInput}
                    placement="top-start"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <Grid className={classes.decision}>
          <DecisionButton onClick={handleSaveButton}>登録</DecisionButton>
        </Grid>
      </div>
      <ConfirmDialog
        open={openSaveConfirmDlg}
        msg={confirmMsgSave}
        onOk={handleSubmitData}
        onCancel={() => setOpenSaveConfirmDlg(false)}
        addClass={{
          contentText: classes.contentText,
        }}
      />
      <InfoDialog open={openInfoDialog} msg={messageSaveSuccess} closeFunc={handleCloseInfoDialog} />
    </>
  );
};

export default UpdateMachine;
